/**
 * Invites API (v0.10.0 - Issue #213)
 * 
 * Handles invite-only access control system.
 * Platform-agnostic - works on web and React Native.
 */

import { apiGet, apiPost, apiPatch, apiDelete } from '../api';

export interface InviteDetails {
  code: string;
  type: 'club_leader' | 'club_member';
  club: {
    id: string;
    name: string;
    logo_url: string | null;
  };
  // #3561 + #3828: on a Bring-a-friend share link the server leaves the sharer
  // out of the response (navigator ruling extending owner Q4) but keeps this
  // field non-null for v1.11.0 clients that read inviter.name unconditionally:
  // id '' and name = the club's name. Branch on is_share_link, never on this.
  inviter: {
    id: string;
    name: string;
  };
  // #3828: true for a Bring-a-friend share link.
  is_share_link: boolean;
  // #3561: present only on a share link. The `?event=` short code resolved
  // within the invite's club, or null when absent or unresolved.
  event?: { title: string; short_code: string } | null;
  // 'opted_out' (#2946): the recipient replied STOP. Terminal, and only they
  // can clear it by texting START.
  status: 'pending' | 'accepted' | 'declined' | 'revoked' | 'expired' | 'opted_out';
  expires_at: number | null;
  // #1014: Multi-use invite fields
  max_uses: number | null;  // NULL = unlimited, 1 = single-use, N = limited
  use_count: number;
}

export interface PendingInvite {
  code: string;
  club: {
    id: string;
    name: string;
    slug: string;
    hero_image_url: string | null;
  };
  inviter: {
    id: string;
    name: string;
  };
  created_at: number;
  expires_at: number | null;
}

export interface PendingInvitesResponse {
  invites: PendingInvite[];
}

export interface InviteDetailsResponse {
  invite: InviteDetails;
}

export interface AcceptInviteResponse {
  message: string;
  club_id: string;
  club_slug: string;
  role?: string;
  existing_role?: string;
}

export interface CreateInvitePayload {
  type: 'club_leader' | 'club_member';
  club_id: string;
  invitee_email?: string;
  invitee_phone?: string;
  expires_in_days?: number;
  // #1014: Multi-use invite support
  // max_uses: null = unlimited, 1 = single-use (default), N = limited uses
  max_uses?: number | null;
  // #1073: Optional label for tracking invite purpose
  label?: string;
}

/**
 * #3384: per-channel outcome of an invite create. The backend commits the
 * invite before sending, so a channel that failed is reported here with a 201
 * (never a 500). 'sent' also covers an email withheld by the invitee's opt-out
 * or a block, deliberately indistinguishable (backend `InviteChannelDelivery`).
 */
export type InviteChannelDelivery = 'sent' | 'failed' | 'not_requested';

export interface InviteDelivery {
  email: InviteChannelDelivery;
  sms: InviteChannelDelivery;
}

export interface InviteResponse {
  // Optional on the client only: a backend that predates #3384 omits it, and
  // an installed native build can talk to either during a deploy.
  delivery?: InviteDelivery;
  invite: {
    id: string;
    code: string;
    type: 'club_leader' | 'club_member';
    club_id: string;
    invitee_email: string | null;
    invitee_phone: string | null;
    status: string;
    expires_at: number | null;
    invite_url: string;
    created_at: number;
    // #1014: Multi-use invite fields
    max_uses: number | null;
    use_count: number;
  };
}

/** #3384: which requested channel(s) of a created invite did not go out. */
export type InviteDeliveryFailure = 'email' | 'text' | 'both';

/**
 * #3384: the channel(s) whose send failed on a created invite, or null when
 * nothing failed (or the backend did not report delivery).
 */
export function inviteDeliveryFailure(res: InviteResponse): InviteDeliveryFailure | null {
  const emailFailed = res.delivery?.email === 'failed';
  const smsFailed = res.delivery?.sms === 'failed';
  if (emailFailed && smsFailed) return 'both';
  if (emailFailed) return 'email';
  if (smsFailed) return 'text';
  return null;
}

/** #3384: user-facing copy for a created invite whose send failed. */
export function inviteDeliveryFailureMessage(failure: InviteDeliveryFailure): string {
  const what = failure === 'both' ? 'email and text' : failure;
  return `Invite created, but the ${what} didn't go out. Copy the link and share it yourself.`;
}

// #3561: POST /clubs/:clubId/invites/share-link
export interface ShareLinkResponse {
  invite: {
    code: string;
    invite_url: string;
    expires_at: number | null;
  };
}

export interface WaitlistSignupPayload {
  email?: string;
  phone?: string;
  source: 'homepage' | 'blocked_signup' | 'other';
  turnstile_token?: string;
}

export interface WaitlistSignupResponse {
  message: string;
  waitlist_id: string;
}

export interface Invite {
  id: string;
  code: string;
  type: string;
  club_id: string;
  invitee_email: string | null;
  invitee_phone: string | null;
  status: string;
  created_at: number;
  accepted_at: number | null;
  expires_at: number | null;
  club_name: string;
  inviter_first_name: string;
  inviter_last_name: string;
  label: string | null; // #1073: Optional label for tracking invite purpose
  // #1066: Multi-use invite fields
  max_uses: number | null; // NULL = unlimited, 1 = single-use, N = limited
  use_count: number;
}

// #1015: Club invite (for club leaders - no club_name since it's for their club)
export interface ClubInvite {
  id: string;
  code: string;
  type: string;
  club_id: string;
  invitee_email: string | null;
  invitee_phone: string | null;
  status: string;
  created_at: number;
  accepted_at: number | null;
  expires_at: number | null;
  inviter_first_name: string;
  inviter_last_name: string;
  max_uses: number | null;
  use_count: number;
}

export interface WaitlistEntry {
  id: string;
  email: string | null;
  phone: string | null;
  source: string;
  created_at: number;
  invited: number;
  invited_at: number | null;
  notes: string | null;
}

export const invitesApi = {
  /**
   * Get pending invites for current user (#907)
   * GET /api/users/me/invites
   */
  getPendingInvites: () =>
    apiGet<PendingInvitesResponse>('/users/me/invites'),

  /**
   * Decline an invite (#907)
   * POST /api/invites/:code/decline
   */
  declineInvite: (code: string) =>
    apiPost<{ message: string }>(`/invites/${code}/decline`, {}),

  /**
   * Check invite validity (public)
   * GET /api/invites/:code
   */
  getInviteDetails: (code: string, eventShortCode?: string | null) =>
    apiGet<InviteDetailsResponse>(
      eventShortCode
        ? `/invites/${code}?${new URLSearchParams({ event: eventShortCode }).toString()}`
        : `/invites/${code}`,
    ),

  /**
   * Accept an invite (authenticated)
   * POST /api/invites/:code/accept
   */
  acceptInvite: (code: string) =>
    apiPost<AcceptInviteResponse>(`/invites/${code}/accept`, {}),

  /**
   * Join waitlist (public)
   * POST /api/invites/waitlist
   */
  joinWaitlist: (payload: WaitlistSignupPayload) => 
    apiPost<WaitlistSignupResponse>('/invites/waitlist', payload),

  /**
   * Create club member invite (club leader)
   * POST /api/clubs/:id/invites
   */
  createClubMemberInvite: (clubId: string, payload: Omit<CreateInvitePayload, 'club_id'>) =>
    apiPost<InviteResponse>(`/clubs/${clubId}/invites`, {
      ...payload,
      club_id: clubId,
    }),

  /**
   * The caller's reusable member link for a club, minted on first use (#3561).
   * POST /api/clubs/:clubId/invites/share-link. Takes the club UUID, not the slug.
   */
  getShareLink: (clubId: string) =>
    apiPost<ShareLinkResponse>(`/clubs/${clubId}/invites/share-link`, {}),

  /**
   * List club invites (club leader) - #1015
   * GET /api/clubs/:id/invites
   */
  listClubInvites: (clubId: string, params?: { status?: string; limit?: number; offset?: number }) => {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    return apiGet<{ invites: ClubInvite[]; count: number; limit: number; offset: number }>(
      `/clubs/${clubId}/invites?${query.toString()}`
    );
  },

  /**
   * Revoke a club invite (club leader) - #1015
   * DELETE /api/clubs/:clubId/invites/:inviteId
   */
  revokeClubInvite: (clubId: string, inviteId: string) =>
    apiDelete<{ message: string }>(`/clubs/${clubId}/invites/${inviteId}`),

  /**
   * Create club leader invite (admin)
   * POST /api/invites/admin/create
   */
  createClubLeaderInvite: (payload: CreateInvitePayload) => 
    apiPost<InviteResponse>('/invites/admin/create', payload),

  /**
   * List all invites (admin)
   * GET /api/invites/admin/list
   */
  listInvites: (params?: {
    status?: string;
    type?: string;
    club_id?: string;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.type) query.append('type', params.type);
    if (params?.club_id) query.append('club_id', params.club_id);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    return apiGet<{ invites: Invite[]; count: number; limit: number; offset: number }>(
      `/invites/admin/list?${query.toString()}`
    );
  },

  /**
   * View waitlist (admin)
   * GET /api/invites/admin/waitlist
   */
  viewWaitlist: (params?: {
    invited?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.invited !== undefined) query.append('invited', params.invited.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    return apiGet<{ waitlist: WaitlistEntry[]; count: number; limit: number; offset: number }>(
      `/invites/admin/waitlist?${query.toString()}`
    );
  },

  /**
   * Revoke an invite (admin)
   * PATCH /api/invites/admin/:id/revoke
   */
  revokeInvite: (inviteId: string) => 
    apiPatch<{ message: string }>(`/invites/admin/${inviteId}/revoke`, {}),
};
